import { Button, Spinner, TextInput } from 'flowbite-react';
import React, { ChangeEvent, ClipboardEvent, useState, KeyboardEvent, useRef, FormEvent, useEffect } from 'react';
import OtpInput from '../shared/OtpInput';
import { formatCareerPathType, InputError } from 'shared-utils';
import { useTimeout } from 'webrix/hooks';
import Container from '../shared/Container';
import { useUserContext } from 'src/context/UserProvider';
import { UserCareerPathType } from 'types';

interface GuestUserOTPProps {
	guestUserOTP: string[];
	setGuestUserOTP: React.Dispatch<React.SetStateAction<string[]>>;
	handleBackToEmail: () => void;
	handleVerifyOtp: () => void;
	guestUserEmail: string;
	handleSendOtp: () => void;
	isLoading: boolean;
	companyName?: string;
	careerpath?: string;
}

const GuestUserOTP: React.FC<GuestUserOTPProps> = ({
	guestUserOTP,
	setGuestUserOTP,
	handleBackToEmail,
	handleVerifyOtp,
	guestUserEmail,
	handleSendOtp,
	isLoading,
	companyName,
	careerpath,
}) => {
	const { user } = useUserContext();
	const [isMobileScreen, setIsMobileScreen] = useState(false);
	const [time, setTime] = useState(30);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	const [isResendEnabled, setIsResendEnabled] = useState(false);
	const { start, stop } = useTimeout(() => {
		if (time > 0) {
			setTime(time - 1);
		} else {
			stop();
			setIsResendEnabled(true);
		}
	}, 1000);

	const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
		const newOtp = [...guestUserOTP];
		newOtp[index] = event.target.value;
		setGuestUserOTP(newOtp);
		if (event.target.value && index < guestUserOTP.length - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};
	useEffect(() => {
		start();
		return () => stop();
	}, [start]);

	const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Backspace' && !guestUserOTP[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		} else if (event.key === 'Enter') {
			handleVerifyOtp();
		}
	};
	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasteData = event.clipboardData.getData('text/plain').slice(0, 6);
		const newOtp = [...guestUserOTP];
		pasteData.split('').forEach((digit, index) => {
			newOtp[index] = digit;
		});
		setGuestUserOTP(newOtp);
	};
	const handleResendCode = () => {
		if (isResendEnabled && time >= 0) {
			setGuestUserOTP(['', '', '', '', '', '']);
			handleSendOtp();
			setTime(30);
			setIsResendEnabled(false);
			start();
		}
	};
	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		handleVerifyOtp();
	};
	return (
		<Container className="py-[144px]">
			<div className="max-w-[560px] px-4 md:px-0 mx-auto rounded-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)] bg-[#FFF] justify-center">
				<div className="items-center text-center content-center max-w-[492px] mx-auto pt-5 md:pt-[36px]">
					<h6 className="ffont-inter text-[20px] font-[700] leading-[24px] text-[#000]">
						Assessment for {careerpath ? formatCareerPathType(careerpath as UserCareerPathType) : ''} role at {companyName}
					</h6>
				</div>
				<div className="flex flex-col items-center gap-[16px] justify-center max-w-[358px] mx-auto pt-[32px]">
					{' '}
					<form onSubmit={handleSubmit}>
						<div className="font-inter text-[16px] weight-[400] leading-[24px] justify-start">
							{'Check the code sent to '}
							<br />
							<span className="dark:text-[#CCDDFF]">{guestUserEmail}</span>
							{' and enter it below.'}
						</div>

						<div
							onClick={() => {
								setGuestUserOTP(['', '', '', '', '', '']);
								handleBackToEmail();
							}}
							className="cursor-pointer text-sm text-[#3568D4] font-[500] underline flex justify-start"
						>
							Change Email
						</div>
						<div className="mt-[16px] flex justify-start gap-[24px]">
							<OtpInput
								isMobileScreen={isMobileScreen}
								handlePaste={handlePaste}
								handleKeyDown={handleKeyDown}
								handleChange={handleChange}
								otp={guestUserOTP}
								inputRefs={inputRefs}
							/>
						</div>
						<div className="mt-[16px] flex text-sm font-normal text-[#3568D4]">
							<div style={{ cursor: time > 0 ? 'not-allowed' : 'pointer' }} onClick={handleResendCode}>
								Resend Code
							</div>

							<div className="ml-[4px]">
								{time > 0 && !isResendEnabled && (
									<div className="cursor-not-allowed">
										{'in '}
										<span className="dark:text-[#C7C9D9] text-[#555770]">{time} sec</span>
									</div>
								)}
							</div>
						</div>
						<div className="hukumu-update-phoneverification-button-container flex items-center justify-center py-[24px]">
							<div className="w-full">
								<Button
									color="purplePlaced"
									disabled={isLoading || guestUserOTP.join('').length < 6}
									type="submit"
									className="mx-auto bg-[#7777E7] w-full max-w-[167px]"
								>
									{isLoading ? <Spinner /> : 'Submit'}
								</Button>
							</div>
						</div>
					</form>
				</div>{' '}
			</div>
		</Container>
	);
};

export default GuestUserOTP;
